import { D6_SCALE } from '@/config/solana';

const PERCENT: bigint = BigInt(100);
const FILL_PRECISION: bigint = BigInt(D6_SCALE);
const MAX_FILL_SCALED: bigint = PERCENT * FILL_PRECISION;

export interface FundingProgress {
  displayPercent: number | null;
  fillPercent: number;
  isFunded: boolean;
}

/**
 * Derive funding progress without converting the source integers through
 * Number. The whole-percent label uses nearest rounding, while the visual fill
 * keeps six decimal places so it moves smoothly between labels.
 */
export function getFundingProgress(totalFunded: string, threshold: string | null): FundingProgress {
  if (threshold === null) return { displayPercent: null, fillPercent: 0, isFunded: false };
  const funded = BigInt(totalFunded.length > 0 ? totalFunded : '0');
  const target = BigInt(threshold.length > 0 ? threshold : '0');

  if (target <= BigInt(0)) {
    return { displayPercent: null, fillPercent: 0, isFunded: false };
  }

  const nonnegativeFunded = funded > BigInt(0) ? funded : BigInt(0);
  const isFunded = nonnegativeFunded >= target;
  const percentNumerator = nonnegativeFunded * PERCENT;
  const wholePercent = percentNumerator / target;
  const remainder = percentNumerator % target;
  const roundedPercent = remainder * BigInt(2) >= target ? wholePercent + BigInt(1) : wholePercent;
  const displayPercent =
    !isFunded && roundedPercent >= PERCENT ? PERCENT - BigInt(1) : roundedPercent;

  const fillScaled = (percentNumerator * FILL_PRECISION) / target;
  const clampedFillScaled = fillScaled > MAX_FILL_SCALED ? MAX_FILL_SCALED : fillScaled;

  return {
    displayPercent: Number(displayPercent),
    fillPercent: Number(clampedFillScaled) / Number(FILL_PRECISION),
    isFunded,
  };
}
